import { Flex } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  IProductComplete,
  IProductPlan,
} from "../../../interfaces/Products/IProductDetail";
import { formatCurrency } from "../../../utils/currency";
import { DollarSquare, Graph, MoneySend } from "iconsax-react";
import { ERecurrencyType } from "../../../interfaces/MyPlans";
import ParameterCard from "./ParameterCard";
import TagNuberXValue from "./TagNuberXValue";
import TagValue from "./TagValue";
import { convertRecurrencyType } from "../../../utils/convertRecurrencyType";

interface IProps {
  selectedProduct: IProductComplete | undefined;
}

export default function ParametersProduct({ selectedProduct }: IProps) {
  const { t } = useTranslation();

  const qtyContract = Array.isArray(selectedProduct?.plan)
    ? (() => {
        const maxQty = selectedProduct?.plan
          .map((p) =>
            typeof p.qty_contract === "number" && p.qty_contract > 1
              ? p.qty_contract
              : undefined
          )
          .filter((q) => q !== undefined)
          .reduce(
            (max, current) => (current! > (max ?? -Infinity) ? current : max),
            undefined as number | undefined
          );
        return maxQty !== undefined ? maxQty : undefined;
      })()
    : undefined;
  const plansLength = selectedProduct?.plan?.length || 0;
  const recurrency_type =
    selectedProduct?.plan?.[0]?.recurrency_type || ERecurrencyType.MONTHLY;
  const firstPlan: IProductPlan =
    selectedProduct?.plan[0] || ({} as IProductPlan);
  const lastPlan: IProductPlan =
    selectedProduct?.plan[selectedProduct?.plan.length - 1] ||
    ({} as IProductPlan);
  const maxParcels = lastPlan?.qty_of_installments || "";
  const isFree = plansLength === 1 && firstPlan?.price === 0;

  const recurrencyTitle =
    recurrency_type === ERecurrencyType.UNIQUE
      ? t("marketplace.detalhe.buy_area.in_cash")
      : `${t("marketplace.detalhe.buy_area.recurrency_type")} ${t(
          convertRecurrencyType(recurrency_type)
        )}`;

  return (
    <Flex
      w="100%"
      gap="20px"
      justify={"space-between"}
      wrap={{ base: "wrap", 991: "wrap", 1300: "nowrap" }}
    >
      <ParameterCard
        title={
          isFree
            ? t("marketplace.detalhe.buy_area.free")
            : plansLength === 1
            ? recurrencyTitle
            : t("marketplace.detalhe.buy_area.max_parcels", {
                x: maxParcels,
              })
        }
        icon={<Graph size={28} />}
        flex={2}
      >
        {isFree ? (
          t("marketplace.detalhe.buy_area.free_description")
        ) : plansLength === 1 ? (
          <TagValue value={formatCurrency(firstPlan?.price)} />
        ) : (
          <Flex
            gap="8px"
            align={"center"}
            wrap={{ base: "wrap", 991: "nowrap" }}
          >
            <TagNuberXValue
              x={firstPlan?.qty_of_installments}
              value={formatCurrency(firstPlan?.price)}
            />
            {t("marketplace.detalhe.buy_area.until")}
            <TagNuberXValue
              x={lastPlan?.qty_of_installments}
              value={formatCurrency(
                (lastPlan?.price || 0) / (lastPlan?.qty_of_installments || 0)
              )}
            />
          </Flex>
        )}
      </ParameterCard>
      <ParameterCard
        title={t("marketplace.detalhe.buy_area.payment_methods_text")}
        icon={<DollarSquare size={28} />}
        disabled={isFree}
      >
        {t("marketplace.detalhe.buy_area.payment_methods")}
      </ParameterCard>
      <ParameterCard
        title={t("marketplace.detalhe.buy_area.leverage")}
        icon={<MoneySend size={28} />}
        disabled={!!!qtyContract}
        maxWidth="350px"
      >
        {!!qtyContract
          ? `${t("marketplace.detalhe.buy_area.until")} ${qtyContract}`
          : t("marketplace.detalhe.buy_area.no_leverage")}
      </ParameterCard>
    </Flex>
  );
}
